import { type FC, useRef } from "react";
import { useColorSlider, useFocusRing, useLocale, VisuallyHidden } from "react-aria";
import { type ColorSliderStateOptions, useColorSliderState } from "react-stately";

import formStyle from "../../styles/form.module.css";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { COLOR_SLIDER_THUMB_SIZE, COLOR_SLIDER_TRACK_THICKNESS } from "./settings.ts";

// props should be AriaColorSliderProps but it's not exposed
export const ColorSlider: FC<Omit<ColorSliderStateOptions, "locale">> = (props) => {
  const { locale } = useLocale();
  const state = useColorSliderState({ ...props, locale });
  const trackRef = useRef(null);
  const inputRef = useRef(null);

  // Default label to the channel name in the current locale
  const label = props.label || state.value.getChannelName(props.channel, locale);

  const { trackProps, thumbProps, inputProps, labelProps, outputProps } = useColorSlider(
    {
      ...props,
      label,
      trackRef,
      inputRef,
    },
    state,
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  return (
    <VStack
      data-testid="color-slider"
      style={{ alignSelf: "stretch", width: 179 }}
      spacing={2}
      // style={props.style}
    >
      <VisuallyHidden>
        <HStack>
          <Label {...labelProps}>{label}</Label>
          <Spacer />
          <div className={formStyle.optional_tag}>
            <output {...outputProps} style={{ flex: "1 0 auto", textAlign: "end" }}>
              {state.value.formatChannelValue(props.channel, locale)}
            </output>
          </div>
        </HStack>
      </VisuallyHidden>
      <div
        {...trackProps}
        ref={trackRef}
        style={{
          ...trackProps.style,
          height: COLOR_SLIDER_TRACK_THICKNESS,
          width: "100%",
          borderRadius: 4,
        }}
      >
        <div
          {...thumbProps}
          data-testid="color-slider-thumb"
          style={{
            ...thumbProps.style,
            top: COLOR_SLIDER_TRACK_THICKNESS / 2,
            border: "2px solid white",
            boxShadow: "0 0 0 1px var(--secondary-dark), inset 0 0 0 1px var(--secondary-dark)",
            width: isFocusVisible ? COLOR_SLIDER_TRACK_THICKNESS + 4 : COLOR_SLIDER_THUMB_SIZE,
            height: isFocusVisible ? COLOR_SLIDER_TRACK_THICKNESS + 4 : COLOR_SLIDER_THUMB_SIZE,
            borderRadius: "50%",
            boxSizing: "border-box",
            background: state.getDisplayColor().toString("css"),
          }}
        >
          <VisuallyHidden>
            <input ref={inputRef} {...inputProps} {...focusProps} />
          </VisuallyHidden>
        </div>
      </div>
    </VStack>
  );
};
